import type { TaskUpdate } from '@/services/types/client-intake-types';
import type { VoiceAIAgentRunSchema } from '@/types/enhanced-bland-call';
import type {
  AlternateContact,
  AlternateContactCollection,
} from '@/types/treatment-request-alternates';

type UnsafeAlternateContact = Omit<AlternateContact, 'value'> & {
  value?: string | null;
};

const STRUCTURED_DATA_REGEX = /STRUCTURED_DATA:\s*(\{[\s\S]*\})/i;

const CONTACT_TYPE_PRIORITY: Record<string, number> = {
  FAX: 4,
  PHONE: 3,
  EMAIL: 2,
  MAILING_ADDRESS: 1,
};

const DEFAULT_CONFIDENCE = 0;

const COMPLETED_STATUS = 'completed';

const confidenceOrDefault = (confidence: number | null | undefined) =>
  typeof confidence === 'number' && Number.isFinite(confidence)
    ? confidence
    : DEFAULT_CONFIDENCE;

/**
 * Convert various handle type casings to a canonical uppercase value.
 */
export const canonicalizeHandleType = (
  handleType: string,
): 'FAX' | 'PHONE' | 'EMAIL' | 'MAILING_ADDRESS' => {
  const normalized = handleType.trim().toUpperCase();
  switch (normalized) {
    case 'FAX':
      return 'FAX';
    case 'PHONE':
      return 'PHONE';
    case 'EMAIL':
      return 'EMAIL';
    case 'MAILING_ADDRESS':
      return 'MAILING_ADDRESS';
    default:
      return 'FAX';
  }
};

/**
 * Normalize a contact value for deduplication comparisons.
 */
export const normalizeAlternateValue = (value: string): string =>
  value
    .replace(/\s+/g, '')
    .replace(/[^a-z0-9]/gi, '')
    .toLowerCase();

/**
 * Deduplicate alternates based on handle type and normalized value, keeping the
 * entry with the highest confidence score.
 */
export const deduplicateAlternates = (
  alternates: AlternateContact[],
): AlternateContact[] => {
  const map = new Map<string, AlternateContact>();

  alternates.forEach((alternate) => {
    const canonicalType = canonicalizeHandleType(alternate.handle_type);
    const key = `${canonicalType}:${normalizeAlternateValue(alternate.value)}`;
    const normalizedAlternate: AlternateContact = {
      ...alternate,
      handle_type: canonicalType,
      confidence: confidenceOrDefault(alternate.confidence),
    };

    const existing = map.get(key);
    if (
      !existing ||
      confidenceOrDefault(existing.confidence) <
        confidenceOrDefault(normalizedAlternate.confidence)
    ) {
      map.set(key, normalizedAlternate);
    }
  });

  return Array.from(map.values());
};

/**
 * Sort alternates by confidence (desc) then handle type priority.
 */
export const sortAlternates = (
  alternates: AlternateContact[],
): AlternateContact[] =>
  [...alternates].sort((a, b) => {
    const confidenceDiff =
      confidenceOrDefault(b.confidence) - confidenceOrDefault(a.confidence);

    if (confidenceDiff !== 0) {
      return confidenceDiff;
    }

    const priorityDiff =
      (CONTACT_TYPE_PRIORITY[canonicalizeHandleType(b.handle_type)] || 0) -
      (CONTACT_TYPE_PRIORITY[canonicalizeHandleType(a.handle_type)] || 0);

    if (priorityDiff !== 0) {
      return priorityDiff;
    }

    return a.value.localeCompare(b.value);
  });

const toAlternate = (alternate: UnsafeAlternateContact): AlternateContact => {
  const safeValue =
    typeof alternate.value === 'string'
      ? alternate.value
      : String(alternate.value ?? '');

  return {
    ...alternate,
    handle_type: canonicalizeHandleType(alternate.handle_type),
    label: alternate.label ?? null,
    confidence: confidenceOrDefault(alternate.confidence),
    value: safeValue.trim(),
  };
};

/**
 * Parse a block of task update text for STRUCTURED_DATA alternates.
 */
export const parseStructuredAlternates = (
  content: string,
): AlternateContact[] => {
  const match = content.match(STRUCTURED_DATA_REGEX);

  if (!match) {
    return [];
  }

  try {
    const parsed = JSON.parse(match[1]);
    const alternates = Array.isArray(parsed?.alternates)
      ? parsed.alternates
      : [];

    return alternates
      .filter(
        (alt: AlternateContact) =>
          alt && typeof alt.value === 'string' && alt.value.trim().length > 0,
      )
      .map(toAlternate);
  } catch (error) {
    console.error('Failed to parse alternate contact structured data', error);
    return [];
  }
};

/**
 * Build an alternate collection from Voice AI runs.
 */
export const collectAlternatesFromRuns = (
  runs: VoiceAIAgentRunSchema[],
): AlternateContactCollection | null => {
  if (!runs?.length) {
    return null;
  }

  const alternateTuples = runs.flatMap((run) => {
    const isCompleted =
      typeof run.status === 'string' &&
      run.status.toLowerCase() === COMPLETED_STATUS;
    if (!isCompleted || !run.evaluation_result?.alternates?.length) {
      return [];
    }

    return (run.evaluation_result.alternates as UnsafeAlternateContact[])
      .filter(
        (alternate) =>
          typeof alternate?.value === 'string' &&
          alternate.value.trim().length > 0,
      )
      .map((alternate) => ({
        alternate: toAlternate(alternate),
        runId: run.id,
        createdAt: run.updated_at || run.created_at || undefined,
      }));
  });

  if (alternateTuples.length === 0) {
    return null;
  }

  const alternates = deduplicateAlternates(
    alternateTuples.map((tuple) => tuple.alternate),
  );

  return {
    alternates: sortAlternates(alternates),
    sourceId: alternateTuples[0].runId,
    sourceLabel: 'Voice AI',
    detectedAt: alternateTuples[0].createdAt,
    origin: 'voice_ai_runs',
  };
};

/**
 * Build an alternate collection from task updates (fallback when Voice AI runs
 * have not yet produced structured alternates).
 */
export const collectAlternatesFromTaskUpdates = (
  updates: TaskUpdate[],
): AlternateContactCollection | null => {
  if (!updates?.length) {
    return null;
  }

  const alternateTuples = updates.flatMap((update) => {
    const alternates = parseStructuredAlternates(update.update || '');
    if (!alternates.length) {
      return [];
    }
    return alternates.map((alternate) => ({
      alternate,
      updateId: update.id,
      createdAt: update.created_at,
    }));
  });

  if (alternateTuples.length === 0) {
    return null;
  }

  const alternates = deduplicateAlternates(
    alternateTuples.map((tuple) => tuple.alternate),
  );

  return {
    alternates: sortAlternates(alternates),
    sourceId: alternateTuples[0].updateId,
    sourceLabel: 'Task Updates',
    detectedAt: alternateTuples[0].createdAt,
    origin: 'task_updates',
  };
};
